import { computed, type ComputedRef, type Ref } from 'vue';

import { AssessmentType, LoanProductType, type AssessmentTypeEnum } from './const';

import type { ApplicationProductType } from '@/api/enums';
import type { LoanApplication } from '@/types';

type ProductType = NonNullable<ApplicationProductType>;

interface UseProductTypeReturn {
  productType: ComputedRef<ProductType>;
  assessmentType: ComputedRef<AssessmentTypeEnum>;
  isCommercialProduct: ComputedRef<boolean>;
  isRegulated: ComputedRef<boolean>;
}

/**
 * Composable for reading product type and assessment type from loan application.
 * Product type and assessment type are determined by backend API and stored in loanApp.
 *
 * @param loanApp - Reactive reference to the loan application
 * @returns productType, assessmentType, isCommercialProduct, and isRegulated computed refs
 */
export function useProductType(loanApp: Ref<LoanApplication>): UseProductTypeReturn {
  const productType = computed<ProductType>(() => loanApp.value.productType ?? LoanProductType.RESIDENTIAL);
  const assessmentType = computed<AssessmentTypeEnum>(() => loanApp.value.assessmentType ?? AssessmentType.REGULATED);
  const isCommercialProduct = computed(() => productType.value === LoanProductType.COMMERCIAL);
  const isRegulated = computed(() => assessmentType.value === AssessmentType.REGULATED);

  return { productType, assessmentType, isCommercialProduct, isRegulated };
}
